import {Box, Skeleton} from '@mui/material';
import dayjs from 'dayjs';
import {QuizSession, User} from '@cp/model';
import ExerciseStatsItem from './ExerciseStatsItem';

export interface ExerciseStatsProps {
  overallAccuracy: number;
  sessions: QuizSession[];
  user?: User;
  loading?: boolean;
}

export default function ExerciseStats({overallAccuracy, sessions, user, loading}: ExerciseStatsProps) {
  const getAccuracyDescription = (accuracy: number) => {
    if (accuracy >= 90) return 'Excellent work!';
    if (accuracy >= 80) return 'Great job!';
    if (accuracy >= 70) return 'Good progress!';
    if (accuracy >= 60) return 'Keep practicing!';
    if (accuracy > 0) return "You're getting there!";
    return 'Start practicing to see your accuracy!';
  };

  const getAccuracyColor = (accuracy: number): 'success.main' | 'secondary.main' | 'primary.main' => {
    if (accuracy >= 80) return 'success.main';
    if (accuracy >= 60) return 'primary.main';
    return 'secondary.main';
  };

  // Calculate questions answered today
  const calculateQuestionsToday = () => {
    if (!sessions || sessions.length === 0) return 0;

    const today = dayjs().format('YYYY-MM-DD');
    let questionsToday = 0;

    sessions.forEach((session) => {
      if (session.endedAt && dayjs(session.endedAt).format('YYYY-MM-DD') === today) {
        questionsToday += session.questions.length;
      }
    });

    return questionsToday;
  };

  const streak = user?.currentStreak || 0;
  const questionsToday = calculateQuestionsToday();

  return (
    <Box sx={{display: 'grid', gridTemplateColumns: {xs: '1fr', md: 'repeat(3, 1fr)'}, gap: 3, mb: 3}}>
      {loading ? (
        <>
          <Skeleton variant="rectangular" height={120} />
          <Skeleton variant="rectangular" height={120} />
          <Skeleton variant="rectangular" height={120} />
        </>
      ) : (
        <>
          <ExerciseStatsItem
            value={`${overallAccuracy}%`}
            label="Overall Accuracy"
            description={getAccuracyDescription(overallAccuracy)}
            color={getAccuracyColor(overallAccuracy)}
          />
          <ExerciseStatsItem
            value={streak}
            label="Day Streak"
            description={streak > 0 ? "Don't break the chain!" : 'Start your streak!'}
            color="secondary.main"
          />
          <ExerciseStatsItem
            value={questionsToday}
            label="Questions Today"
            description={questionsToday > 0 ? 'Great progress today!' : 'Start studying!'}
            color="primary.main"
          />
        </>
      )}
    </Box>
  );
}
